import { jsx } from 'react/jsx-runtime';
import { useEventCallback, useResizeObserver } from '@flex-design-system/fx-utils/hooks';
import { styled } from '@flex-design-system/stitches-react';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { createContextScope } from '@radix-ui/react-context';
import { useLayoutEffect } from '@radix-ui/react-use-layout-effect';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useState, useRef } from 'react';
import { heightVar, root } from './dynamicHeightTransition.vanilla.mjs';

const HEIGHT_TRANSITION_ROOT_NAME = "DynamicHeightTransitionRoot";
const [createHeightTransitionContext, createHeightTransitionScope] = createContextScope(HEIGHT_TRANSITION_ROOT_NAME);
const [ContentTransitionProvider, useContentTransitionContext] = createHeightTransitionContext(
  HEIGHT_TRANSITION_ROOT_NAME
);
const DynamicHeightTransitionImpl = forwardRef(function DynamicHeightTransition({ children, __scopeContentTransition, className, style, ...props }, forwardedRef) {
  const [contentRef, setContentRef] = useState(null);
  const [height, setHeight] = useState(null);
  const heightDiff = useRef(0);
  const contentHeight = height !== null ? height + "px" : void 0;
  const handleSizeChange = useEventCallback(() => {
    if (contentRef) {
      if (height !== contentRef.offsetHeight) {
        heightDiff.current = Math.abs((height ?? 0) - heightDiff.current);
      }
      setHeight(contentRef.offsetHeight);
    }
  });
  useLayoutEffect(() => {
    handleSizeChange();
  }, [handleSizeChange]);
  useResizeObserver(contentRef, handleSizeChange);
  return /* @__PURE__ */ jsx(
    ContentTransitionProvider,
    {
      scope: __scopeContentTransition,
      setContentRef,
      children: /* @__PURE__ */ jsx(
        Root,
        {
          ref: forwardedRef,
          className: classNames(
            root({ speed: heightDiff.current > 100 ? "slow" : "quick" }),
            className
          ),
          style: {
            ...assignInlineVars({ [heightVar]: contentHeight }),
            ...style
          },
          ...props,
          children
        }
      )
    }
  );
});
const Root = styled("div");
const ContentRoot = styled("div");
ContentRoot.toString = () => "dynamic-height-transition-content";
const CONTENT_TRANSITION_CONTENT_NAME = "DynamicContentTransitionContent";
const Content = forwardRef(
  function Content2({ children, __scopeContentTransition, ...props }, forwardedRef) {
    const { setContentRef } = useContentTransitionContext(
      CONTENT_TRANSITION_CONTENT_NAME,
      __scopeContentTransition
    );
    const composedRefs = useComposedRefs(forwardedRef, setContentRef);
    return /* @__PURE__ */ jsx(ContentRoot, { ref: composedRefs, ...props, children });
  }
);
const DynamicHeightTransition2 = Object.assign(DynamicHeightTransitionImpl, {
  Content
});

export { createHeightTransitionScope as createContentTransitionScope, DynamicHeightTransition2 as default };
//# sourceMappingURL=DynamicHeightTransition.mjs.map
